import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatValue } from '../utils/formatters';
import './ForecastTable.css';

/**
 * Compute subtotal values for a set of rows by summing per-period values.
 */
const computeSubtotalValues = (rows, visiblePeriods) => {
    const values = {};
    visiblePeriods.forEach(period => {
        let sum = 0;
        let hasValue = false;
        rows.forEach(row => {
            const val = row.values?.[period];
            if (typeof val === 'number') { sum += val; hasValue = true; }
        });
        if (hasValue) values[period] = sum;
    });
    return Object.keys(values).length > 0 ? values : null;
};

/**
 * Compute the sort value for a row.
 * Prefers the sum of forecast-period values; falls back to the sum of ALL cached
 * values when forecast periods are absent or all zero (e.g. actuals-only rows,
 * or forecasts not yet hydrated).
 */
const getForecastTotal = (row, forecastPeriods) => {
    if (!row?.values) return 0;

    // Try forecast periods first
    if (forecastPeriods?.length) {
        let forecastSum = 0;
        forecastPeriods.forEach(period => {
            const val = row.values?.[period];
            if (typeof val === 'number') forecastSum += val;
        });
        if (forecastSum !== 0) return forecastSum;
    }

    // Fall back to sum of all available cached values
    let totalSum = 0;
    Object.values(row.values).forEach(val => {
        if (typeof val === 'number') totalSum += val;
    });
    return totalSum;
};

/**
 * Check if a row is an "other" / remainder row (definition_id === 'other_row').
 */
const isOtherRow = (row) => row?.definition_id === 'other_row';

/**
 * Sort rows by display order:
 *   1. Rows with display_order (structural/fixed) come first, sorted by that value.
 *   2. Agent-created rows (no display_order) come after, sorted by absolute 13-week
 *      forecast total (largest first), with other_row always last.
 */
const sortRowsByForecastTotal = (rows, forecastPeriods) => {
    const fixed = rows.filter(r => r.display_order != null).sort((a, b) => a.display_order - b.display_order);
    const dynamic = rows.filter(r => r.display_order == null).sort((a, b) => {
        const aIsOther = isOtherRow(a);
        const bIsOther = isOtherRow(b);
        if (aIsOther && !bIsOther) return 1;
        if (!aIsOther && bIsOther) return -1;
        if (aIsOther && bIsOther) return 0;
        return Math.abs(getForecastTotal(b, forecastPeriods)) - Math.abs(getForecastTotal(a, forecastPeriods));
    });
    return [...fixed, ...dynamic];
};

/**
 * Build a virtual _drivers sub-group for a set of row IDs, pulling from the master drivers list.
 * Returns null if no matching drivers found.
 */
const buildDriversSubGroup = (id, rowIds, allDriverItems) => {
    const rowIdSet = new Set(rowIds);
    const matching = allDriverItems.filter(d => rowIdSet.has(d._parentRowId));
    if (matching.length === 0) return null;
    return {
        id,
        kind: 'group',
        label: 'Drivers',
        valueType: 'currency',
        start_collapsed: true,
        _isVirtualDriverGroup: true,
        children: matching
    };
};

/**
 * Organize a group's row children into display sub-groups based on the `group` field.
 * Rows sharing the same `group` label are collected under a virtual collapsible sub-group
 * with a computed subtotal. Drivers are redistributed so each display group gets its own
 * collapsed Drivers section. Rows without a `group` stay at the group level.
 */
const organizeIntoDisplayGroups = (groupNode, visiblePeriods, actualPeriodCount = 0) => {
    if (!groupNode.children || groupNode.children.length === 0) return groupNode;
    
    // Don't reorganize virtual driver groups themselves
    if (groupNode._isVirtualDriverGroup) return groupNode;
    
    // Forecast periods for sorting (periods after actuals)
    const forecastPeriods = visiblePeriods.slice(actualPeriodCount);
    
    // Skip summary ref-child groups (display groups don't apply to summary)
    const rowChildren = groupNode.children.filter(c => c.kind === 'row' && !c._isDriverDisplay);
    if (rowChildren.length > 0 && rowChildren.every(r => r._isRefChild)) return groupNode;
    const hasGroupedRows = rowChildren.some(r => r.group);
    
    // Even if there are no grouped rows, we still sort individual rows by forecast total
    if (!hasGroupedRows) {
        // Sort all row children by forecast total, other_row last
        const sortedRowChildren = sortRowsByForecastTotal(rowChildren, forecastPeriods);
        const nonRowChildren = groupNode.children.filter(c => c.kind !== 'row' || c._isDriverDisplay);
        return { ...groupNode, children: [...sortedRowChildren, ...nonRowChildren] };
    }
    
    // Collect all driver display items from the master _drivers group (if it exists)
    const driversGroup = groupNode.children.find(c => c._isVirtualDriverGroup);
    const allDriverItems = driversGroup?.children || [];
    
    // Everything else that isn't a plain row or the drivers group (pass through)
    const otherChildren = groupNode.children.filter(
        c => (c.kind !== 'row' || c._isDriverDisplay) && !c._isVirtualDriverGroup
    );
    
    // Bucket rows by group label
    const groupBuckets = new Map(); // groupLabel -> [rows]
    const ungroupedRows = [];
    
    rowChildren.forEach(row => {
        if (row.group) {
            if (!groupBuckets.has(row.group)) groupBuckets.set(row.group, []);
            groupBuckets.get(row.group).push(row);
        } else {
            ungroupedRows.push(row);
        }
    });
    
    // Build virtual display groups (rows sorted inside + per-group drivers)
    const displayGroups = [];
    groupBuckets.forEach((rows, label) => {
        const slugId = label.toLowerCase().replace(/[^a-z0-9]+/g, '_');
        const rowIds = rows.map(r => r.id);
        const driversNode = buildDriversSubGroup(`_dg_${slugId}_drivers`, rowIds, allDriverItems);
        
        // Sort rows inside the display group by forecast total (other_row last)
        const sortedRows = sortRowsByForecastTotal(rows, forecastPeriods);
        
        displayGroups.push({
            id: `_dg_${slugId}`,
            kind: 'group',
            label: label,
            valueType: 'currency',
            _isDisplayGroup: true,
            start_collapsed: true,
            values: computeSubtotalValues(sortedRows, visiblePeriods),
            children: driversNode ? [...sortedRows, driversNode] : sortedRows
        });
    });
    
    // Sort ungrouped rows (other_row last)
    const sortedUngroupedRows = sortRowsByForecastTotal(ungroupedRows, forecastPeriods);
    
    // Build a drivers group for ungrouped rows (if any have drivers)
    const ungroupedRowIds = sortedUngroupedRows.map(r => r.id);
    const ungroupedDrivers = buildDriversSubGroup('_dg_ungrouped_drivers', ungroupedRowIds, allDriverItems);

    // Mix display groups and ungrouped rows into one pool sorted by absolute magnitude.
    // For a display group, its sort value is the sum of its member rows.
    // other_row / all-other groups always sink to the bottom.
    const getItemSortValue = (item) => {
        if (item.kind === 'group' && item._isDisplayGroup) {
            const rows = item.children.filter(c => c.kind === 'row' && !c._isDriverDisplay);
            return rows.reduce((sum, r) => sum + getForecastTotal(r, forecastPeriods), 0);
        }
        return getForecastTotal(item, forecastPeriods);
    };

    const isItemOther = (item) => {
        if (item.kind === 'group' && item._isDisplayGroup) {
            const rows = item.children.filter(c => c.kind === 'row' && !c._isDriverDisplay);
            return rows.length > 0 && rows.every(r => isOtherRow(r));
        }
        return isOtherRow(item);
    };

    // Build the mixed pool: display groups + ungrouped row blocks
    // ungrouped rows are already sorted among themselves; insert them individually
    const mixedPool = [...displayGroups, ...sortedUngroupedRows];
    mixedPool.sort((a, b) => {
        const aOther = isItemOther(a);
        const bOther = isItemOther(b);
        if (aOther && !bOther) return 1;
        if (!aOther && bOther) return -1;
        return Math.abs(getItemSortValue(b)) - Math.abs(getItemSortValue(a));
    });

    // Re-append ungrouped drivers after their rows wherever they ended up
    const finalChildren = [
        ...mixedPool,
        ...(ungroupedDrivers ? [ungroupedDrivers] : []),
        ...otherChildren
    ];
    
    return { ...groupNode, children: finalChildren };
};

/**
 * Renders account-level drilldown rows inside the panel — one grid row per account,
 * period-aligned to the same calendar as the parent table.
 * NOTE: must be defined BEFORE renderViewNode so the const is in scope.
 */
const SKELETON_ROW_COUNT = 3;

const DrilldownContent = ({ nodeId, drilldownData, visiblePeriods, gridTemplateColumns, firstForecastIndex, onAccountClick }) => {
    const state = drilldownData.get(nodeId);
    if (!state) return null;

    if (state.loading) {
        return (
            <div className="drilldown-account-rows">
                {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                    <div
                        key={`skel-${i}`}
                        className="table-row drilldown-subrow drilldown-skeleton-row"
                        style={{ gridTemplateColumns }}
                    >
                        <div className="row-label drilldown-subrow-label">
                            <div className="skeleton-bar skeleton-name" style={{ width: `${60 + i * 15}%` }} />
                            <div className="skeleton-bar skeleton-sub" style={{ width: `${40 + i * 10}%` }} />
                        </div>
                        {visiblePeriods.map((period, idx) => (
                            <div key={period} className={`data-cell drilldown-data-cell ${idx === firstForecastIndex ? 'first-forecast' : ''}`}>
                                <div className="skeleton-bar skeleton-val" />
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    }
    if (state.error) {
        return <div className="drilldown-status drilldown-error">{state.error}</div>;
    }
    if (!state.accounts || state.accounts.length === 0) {
        return <div className="drilldown-status">No account detail available for this row.</div>;
    }

    return (
        <div className="drilldown-account-rows">
            {state.accounts.map((acct) => (
                <div
                    key={acct.id}
                    className="table-row drilldown-subrow drilldown-subrow-clickable"
                    style={{ gridTemplateColumns }}
                    onClick={() => onAccountClick(acct)}
                    title={`View transactions for ${acct.name}`}
                >
                    <div className="row-label drilldown-subrow-label">
                        <span className="drilldown-subrow-name">{acct.name}</span>
                        {acct.subtype && (
                            <span className="drilldown-subtype">{acct.subtype}</span>
                        )}
                        <span className="drilldown-txn-hint">→</span>
                    </div>
                    {visiblePeriods.map((period, idx) => {
                        const val = acct.values?.[period];
                        return (
                            <div
                                key={period}
                                className={`data-cell drilldown-data-cell ${idx === firstForecastIndex ? 'first-forecast' : ''} ${typeof val === 'number' && val < 0 ? 'negative' : ''}`}
                            >
                                {typeof val === 'number'
                                    ? formatValue(val, 'currency')
                                    : null}
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

/**
 * Render children of a group node, inserting driver_group header rows
 * when the driver_group label changes between consecutive driver children.
 */
const renderGroupChildren = (parentNode, childDepth, visiblePeriods, gridTemplateColumns, firstForecastIndex, expandedGroups, toggleGroup, expandedDrilldowns, toggleDrilldown, drilldownData, onAccountClick, onMethodologyEnter, onMethodologyLeave) => {
    const children = parentNode.children;
    const hasDriverGroups = parentNode._isVirtualDriverGroup && children.some(c => c.driver_group);

    if (!hasDriverGroups) {
        return children.map(child => renderViewNode(child, childDepth, visiblePeriods, gridTemplateColumns, firstForecastIndex, expandedGroups, toggleGroup, expandedDrilldowns, toggleDrilldown, drilldownData, onAccountClick, onMethodologyEnter, onMethodologyLeave));
    }

    let lastGroup = null;
    return children.flatMap((child) => {
        const elements = [];
        if (child.driver_group && child.driver_group !== lastGroup) {
            elements.push(
                <div
                    key={`dg-${parentNode.id}-${child.driver_group}`}
                    className="table-row driver-group-separator"
                    style={{ gridTemplateColumns }}
                >
                    <div className="row-label" style={{ paddingLeft: childDepth * 20 }}>
                        <span className="driver-group-label">{child.driver_group}</span>
                    </div>
                    {visiblePeriods.map((period, idx) => (
                        <div key={period} className={`data-cell ${idx === firstForecastIndex ? 'first-forecast' : ''}`}></div>
                    ))}
                </div>
            );
            lastGroup = child.driver_group;
        }
        elements.push(renderViewNode(child, childDepth, visiblePeriods, gridTemplateColumns, firstForecastIndex, expandedGroups, toggleGroup, expandedDrilldowns, toggleDrilldown, drilldownData, onAccountClick, onMethodologyEnter, onMethodologyLeave));
        return elements;
    });
};

/**
 * Recursively render view nodes (groups and rows) in grid format
 */
const renderViewNode = (node, depth, visiblePeriods, gridTemplateColumns, firstForecastIndex, expandedGroups, toggleGroup, expandedDrilldowns, toggleDrilldown, drilldownData, onAccountClick, onMethodologyEnter, onMethodologyLeave) => {
    if (!node) return null;

    const isGroup = node.kind === 'group';
    const hasChildren = node.children && node.children.length > 0;
    const isCollapsible = isGroup; // All groups are collapsible (even empty ones)
    const isExpanded = expandedGroups.has(node.id);

    // A drilldown-eligible row: leaf row (not a group, not a driver display item)
    const isDrilldownRow = !isGroup && !node._isDriverDisplay;
    const isDrilldownOpen = isDrilldownRow && expandedDrilldowns.has(node.id);
    
    // For groups without values (like driver sections), render header row only
    if (isGroup && !node.values) {
        return (
            <React.Fragment key={node.id}>
                <div 
                    className={`table-row group-header-row depth-${depth} ${isCollapsible ? 'collapsible' : ''}`}
                    style={{ gridTemplateColumns }}
                    onClick={isCollapsible ? () => toggleGroup(node.id) : undefined}
                >
                    <div className="row-label" style={{ paddingLeft: depth * 20 }}>
                        <div className="row-label-text">{node.label}</div>
                        {isCollapsible && (
                            <span className={`group-arrow ${isExpanded ? 'expanded' : 'collapsed'}`}>
                                {isExpanded ? '▾' : '▸'}
                            </span>
                        )}
                    </div>
                    {/* Empty cells for period columns */}
                    {visiblePeriods.map((period, idx) => (
                        <div key={period} className={`data-cell ${idx === firstForecastIndex ? 'first-forecast' : ''}`}></div>
                    ))}
                </div>
                {isExpanded && (
                    hasChildren ? (
                        renderGroupChildren(node, depth + 1, visiblePeriods, gridTemplateColumns, firstForecastIndex, expandedGroups, toggleGroup, expandedDrilldowns, toggleDrilldown, drilldownData, onAccountClick, onMethodologyEnter, onMethodologyLeave)
                    ) : (
                        // Render blank placeholder row for empty groups
                        <div 
                            key={`${node.id}-placeholder`}
                            className={`table-row placeholder-row depth-${depth + 1}`}
                            style={{ gridTemplateColumns }}
                        >
                            <div className="row-label" style={{ paddingLeft: (depth + 1) * 20 }}></div>
                            {visiblePeriods.map((period, idx) => (
                                <div key={period} className={`data-cell ${idx === firstForecastIndex ? 'first-forecast' : ''}`}></div>
                            ))}
                        </div>
                    )
                )}
            </React.Fragment>
        );
    }

    // Render data row (group or row with values)
    const isDisplayGroup = isGroup && node._isDisplayGroup;
    const notesSummary = node.notes?.summary || node.methodology_note;
    const hasMethodologyNote = !isGroup && !!notesSummary;
    const isDriverRow = !!node._isDriverDisplay;
    return (
        <React.Fragment key={node.id}>
            <div 
                className={`table-row ${isGroup ? 'group-row' : 'data-row'} depth-${depth} ${isCollapsible ? 'collapsible' : ''} ${isDrilldownRow ? 'drilldown-eligible' : ''} ${isDrilldownOpen ? 'drilldown-open' : ''} ${node.emphasized ? 'emphasized' : ''} ${isDisplayGroup ? 'display-group-row' : ''} ${isDriverRow ? 'driver-row' : ''}`}
                {...(node.driver_group && { 'data-driver-group': node.driver_group })}
                style={{ gridTemplateColumns }}
                onClick={isCollapsible ? () => toggleGroup(node.id) : isDrilldownRow ? () => toggleDrilldown(node.id, node) : undefined}
            >
                <div className="row-label" style={{ paddingLeft: depth * 20 + (node.indent_level || 0) * 12 }}>
                    <div className="row-label-text">
                        {isGroup ? <strong>{node.label}</strong> : node.label}
                    </div>
                    {hasMethodologyNote && (
                        <span
                            className="methodology-note-indicator"
                            onClick={(e) => e.stopPropagation()}
                            onMouseEnter={(e) => onMethodologyEnter(e, notesSummary)}
                            onMouseLeave={onMethodologyLeave}
                        >ⓘ</span>
                    )}
                    {isCollapsible && (
                        <span className={`group-arrow ${isExpanded ? 'expanded' : 'collapsed'}`}>
                            {isExpanded ? '▾' : '▸'}
                        </span>
                    )}
                    {isDrilldownRow && (
                        <span className={`drilldown-arrow ${isDrilldownOpen ? 'open' : ''}`}>
                            {isDrilldownOpen ? '▾' : '▸'}
                        </span>
                    )}
                </div>
                    {visiblePeriods.map((period, idx) => {
                        const cellVal = node.values?.[period];
                        return (
                            <div key={period} className={`data-cell ${idx === firstForecastIndex ? 'first-forecast' : ''} ${typeof cellVal === 'number' && cellVal < 0 ? 'negative' : ''}`}>
                                {formatValue(cellVal, node.valueType)}
                            </div>
                        );
                    })}
            </div>
            {isDrilldownOpen && (
                <div className="drilldown-panel" key={`${node.id}-drilldown`}>
                    <DrilldownContent
                        nodeId={node.id}
                        drilldownData={drilldownData}
                        visiblePeriods={visiblePeriods}
                        gridTemplateColumns={gridTemplateColumns}
                        firstForecastIndex={firstForecastIndex}
                        onAccountClick={onAccountClick}
                    />
                </div>
            )}
            {isExpanded && (
                hasChildren ? (
                    node.children.map(child => renderViewNode(child, depth + 1, visiblePeriods, gridTemplateColumns, firstForecastIndex, expandedGroups, toggleGroup, expandedDrilldowns, toggleDrilldown, drilldownData, onAccountClick, onMethodologyEnter, onMethodologyLeave))
                ) : isGroup ? (
                    // Render blank placeholder row for empty groups with values
                    <div 
                        key={`${node.id}-placeholder`}
                        className={`table-row placeholder-row depth-${depth + 1}`}
                        style={{ gridTemplateColumns }}
                    >
                        <div className="row-label" style={{ paddingLeft: (depth + 1) * 20 }}></div>
                        {visiblePeriods.map((period, idx) => (
                            <div key={period} className={`data-cell ${idx === firstForecastIndex ? 'first-forecast' : ''}`}></div>
                        ))}
                    </div>
                ) : null
            )}
        </React.Fragment>
    );
};

/**
 * Forecast Table Component
 * Renders a component's hierarchical view structure in a grid layout (no table headers)
 */
const ForecastTable = ({ 
    component, 
    visiblePeriods,
    gridTemplateColumns,
    actualPeriodCount = 0,
    forecastId,
    workspaceId,
    calendar,
}) => {
    const navigate = useNavigate();
    // Preprocess the view to organize rows into display groups and sort by forecast total
    const processedView = useMemo(() => {
        if (!component.view) return null;
        return {
            ...component.view,
            children: (component.view.children || []).map(child => {
                if (child.kind !== 'group') return child;
                return organizeIntoDisplayGroups(child, visiblePeriods, actualPeriodCount);
            })
        };
    }, [component.view, visiblePeriods, actualPeriodCount]);

    // State to track expanded/collapsed groups
    const [expandedGroups, setExpandedGroups] = useState(() => {
        // Initialize groups based on start_collapsed flag
        const initialExpanded = new Set();
        
        const collectGroupIds = (node) => {
            if (node.kind === 'group') {
                // Only add to expanded if NOT marked as start_collapsed
                if (!node.start_collapsed) {
                    initialExpanded.add(node.id);
                }
                if (node.children && node.children.length > 0) {
                    node.children.forEach(collectGroupIds);
                }
            }
        };
        
        if (processedView && processedView.children) {
            processedView.children.forEach(collectGroupIds);
        }
        
        return initialExpanded;
    });

    const toggleGroup = (groupId) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    };

    const [expandedDrilldowns, setExpandedDrilldowns] = useState(new Set());

    // Cache: rowId -> { loading, accounts, error }
    const [drilldownData, setDrilldownData] = useState(new Map());

    const fetchSubaccounts = useCallback(async (rowId, node) => {
        const actualsSource = node?.actuals_source;
        if (!actualsSource?.core_table || !workspaceId) return;

        setDrilldownData(prev => new Map(prev).set(rowId, { loading: true, accounts: null, error: null }));
        try {
            const payload = {
                workspaceId,
                forecastId: forecastId || null,
                actualsSource,
                calendar: calendar || null,
            };
            // For other_row, include the exclude_row_ids so the backend can filter them out
            if (node.definition_id === 'other_row' && node.definition_inputs?.exclude_row_ids) {
                payload.excludeRowIds = node.definition_inputs.exclude_row_ids;
            }
            const { data } = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/components/subaccounts`,
                payload,
                { withCredentials: true }
            );
            setDrilldownData(prev => new Map(prev).set(rowId, { loading: false, accounts: data.accounts || [], error: null }));
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Failed to load accounts';
            setDrilldownData(prev => new Map(prev).set(rowId, { loading: false, accounts: null, error: msg }));
        }
    }, [workspaceId, forecastId, calendar]);

    // Refs so toggleDrilldown always reads latest without stale closures
    const drilldownDataRef = React.useRef(drilldownData);
    React.useEffect(() => { drilldownDataRef.current = drilldownData; }, [drilldownData]);
    const expandedDrilldownsRef = React.useRef(expandedDrilldowns);
    React.useEffect(() => { expandedDrilldownsRef.current = expandedDrilldowns; }, [expandedDrilldowns]);

    const toggleDrilldown = useCallback((rowId, node) => {
        const isCurrentlyOpen = expandedDrilldownsRef.current.has(rowId);
        setExpandedDrilldowns(prev => {
            const next = new Set(prev);
            if (isCurrentlyOpen) {
                next.delete(rowId);
            } else {
                next.add(rowId);
            }
            return next;
        });
        if (!isCurrentlyOpen && !drilldownDataRef.current.has(rowId)) {
            fetchSubaccounts(rowId, node);
        }
    }, [fetchSubaccounts]);

    const handleAccountClick = useCallback((acct) => {
        const params = new URLSearchParams();
        if (acct.name) params.set('sourceAccounts', acct.name);
        navigate(`/forecast/transactions?${params.toString()}`);
    }, [navigate]);

    const [methodologyTooltip, setMethodologyTooltip] = useState({ visible: false, note: '', x: 0, y: 0 });

    const handleMethodologyEnter = useCallback((e, note) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setMethodologyTooltip({ visible: true, note, x: rect.right + 8, y: rect.top - 4 });
    }, []);

    const handleMethodologyLeave = useCallback(() => {
        setMethodologyTooltip(prev => ({ ...prev, visible: false }));
    }, []);

    if (!processedView || !processedView.children) {
        return <div className="component-empty">No data available</div>;
    }

    // Calculate the index of the first forecast period in visiblePeriods
    const firstForecastIndex = actualPeriodCount;

    return (
        <div className="forecast-component-table">
            {component.header && (
                <div className="component-header-row" style={{ gridTemplateColumns }}>
                    <div className="component-header-label">
                        <h3>{component.header.toUpperCase()}</h3>
                    </div>
                    {visiblePeriods.map((period, idx) => (
                        <div key={period} className={`component-header-cell ${idx === firstForecastIndex ? 'first-forecast' : ''}`}></div>
                    ))}
                </div>
            )}
            
            <div className="forecast-table-grid">
                {processedView.children.map(node => renderViewNode(node, 0, visiblePeriods, gridTemplateColumns, firstForecastIndex, expandedGroups, toggleGroup, expandedDrilldowns, toggleDrilldown, drilldownData, handleAccountClick, handleMethodologyEnter, handleMethodologyLeave))}
            </div>

            {methodologyTooltip.visible && (
                <div
                    className="methodology-tooltip-popup"
                    style={{ left: methodologyTooltip.x, top: methodologyTooltip.y }}
                >
                    {methodologyTooltip.note}
                </div>
            )}
        </div>
    );
};

export default ForecastTable;

